import { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import { AbstractCommand } from '../AbstractCommand';

import type { VideoEditor } from '../../VideoEditor';

/**
 * Merges two clips by removing break that occur immediately after specified
 * transcript token id.
 * Semantics: provided token id is interpreted as the last token of a clip we
 * want to merge with the subsequent clip (if any).
 */
export class RemoveKaraokeBreakCommand extends AbstractCommand {
  private transcriptTokenId: string;
  private prevClips: KaraokeClip[] | null = null;

  constructor(editor: VideoEditor, transcriptTokenId: string) {
    super(editor);
    this.transcriptTokenId = transcriptTokenId;
  }

  execute(): boolean {
    if (!this.transcriptTokenId) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const clips = track.karaokeClips;
    if (clips.length === 0) return false;

    // for undo
    this.prevClips = [...clips];

    const newClips: KaraokeClip[] = [];

    for (let i = 0; i < clips.length; i++) {
      const current = clips[i];
      const lastToken =
        current.transcriptTokens[current.transcriptTokens.length - 1];
      // If current last token id is in set AND there is a next clip, merge
      if (this.transcriptTokenId === lastToken.id && i < clips.length - 1) {
        const next = clips[i + 1];
        const merged = this.mergeTwoClips(current, next);
        newClips.push(merged);
        i++; // skip next clip (consumed)
      } else {
        newClips.push(current);
      }
    }

    track.karaokeClips = newClips;

    this.editor.markDirty('karaoke_remove_breaks');
    return true;
  }

  private mergeTwoClips(current: KaraokeClip, next: KaraokeClip) {
    return new KaraokeClip({
      id: current.id, // preserve id of first clip in merged pair
      transcriptTokens: [...current.transcriptTokens, ...next.transcriptTokens],
      hiddenTranscriptTokenIds: [
        ...current.hiddenTranscriptTokenIds,
        ...next.hiddenTranscriptTokenIds.filter(
          id => !current.hiddenTranscriptTokenIds.includes(id),
        ),
      ],
      ignoredByFiltersIds: [
        ...current.ignoredByFiltersIds,
        ...next.ignoredByFiltersIds.filter(
          id => !current.ignoredByFiltersIds.includes(id),
        ),
      ],
      startSecondsOffset: current.startSecondsOffset,
      endSecondsOffset: next.endSecondsOffset,
      karaokeConfig: current.karaokeConfig, // keep first config (could later choose smarter merge)
      track: current.track,
    });
  }

  undo(): boolean {
    if (!this.prevClips) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    track.karaokeClips = this.prevClips;

    this.editor.markDirty('karaoke_remove_breaks_undo');
    return true;
  }

  getName(): string {
    return 'RemoveKaraokeBreakCommand';
  }

  getDescription(): string {
    return 'Merges karaoke clips by removing specified breaks';
  }
}
